import type { DeviceRepository } from '../ports';
import type { MediaDeviceDescriptor } from '@shared/types';

/**
 * Browser/WebRTC-backed implementation of DeviceRepository. This is the
 * only file in the feature that touches `navigator.mediaDevices` for
 * enumeration. Swap this class out (and rebind it in the composition root)
 * to change the device source without touching any UI code.
 */
export class WebRtcDeviceRepository implements DeviceRepository {
  async listVideoInputs(): Promise<MediaDeviceDescriptor[]> {
    return this.listByKind('videoinput');
  }

  async listAudioInputs(): Promise<MediaDeviceDescriptor[]> {
    return this.listByKind('audioinput');
  }

  onDeviceChange(callback: () => void): () => void {
    navigator.mediaDevices.addEventListener('devicechange', callback);
    return () => navigator.mediaDevices.removeEventListener('devicechange', callback);
  }

  private async listByKind(kind: 'videoinput' | 'audioinput'): Promise<MediaDeviceDescriptor[]> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((device) => device.kind === kind)
      .map((device, index) => ({
        id: device.deviceId,
        label: device.label || this.fallbackLabel(kind, index),
        kind,
      }));
  }

  private fallbackLabel(kind: 'videoinput' | 'audioinput', index: number): string {
    // Labels are blank until permission is granted; provide a stable
    // placeholder so the UI never renders an empty string.
    return kind === 'videoinput' ? `Camera ${index + 1}` : `Microphone ${index + 1}`;
  }
}
